import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Code, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const CppEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapeCpp = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Escape backslashes first to avoid conflicts
    const backslashMatches = result.match(/\\/g);
    if (backslashMatches) {
      entitiesCount += backslashMatches.length;
      result = result.replace(/\\/g, '\\\\');
    }

    // Escape double quotes
    const doubleQuoteMatches = result.match(/"/g);
    if (doubleQuoteMatches) {
      entitiesCount += doubleQuoteMatches.length;
      result = result.replace(/"/g, '\\"');
    }

    // Escape single quotes (for char literals)
    const singleQuoteMatches = result.match(/'/g);
    if (singleQuoteMatches) {
      entitiesCount += singleQuoteMatches.length;
      result = result.replace(/'/g, "\\'");
    }

    // Escape newlines
    const newlineMatches = result.match(/\n/g);
    if (newlineMatches) {
      entitiesCount += newlineMatches.length;
      result = result.replace(/\n/g, '\\n');
    }

    // Escape carriage returns
    const carriageReturnMatches = result.match(/\r/g);
    if (carriageReturnMatches) {
      entitiesCount += carriageReturnMatches.length;
      result = result.replace(/\r/g, '\\r');
    }

    // Escape tabs
    const tabMatches = result.match(/\t/g);
    if (tabMatches) {
      entitiesCount += tabMatches.length;
      result = result.replace(/\t/g, '\\t');
    }

    // Escape backspace
    const backspaceMatches = result.match(/\b/g);
    if (backspaceMatches) {
      entitiesCount += backspaceMatches.length;
      result = result.replace(/\b/g, '\\b');
    }

    // Escape form feed
    const formFeedMatches = result.match(/\f/g);
    if (formFeedMatches) {
      entitiesCount += formFeedMatches.length;
      result = result.replace(/\f/g, '\\f');
    }

    // Escape vertical tab
    const verticalTabMatches = result.match(/\v/g);
    if (verticalTabMatches) {
      entitiesCount += verticalTabMatches.length;
      result = result.replace(/\v/g, '\\v');
    }

    // Escape alert/bell character
    const alertMatches = result.match(/\x07/g);
    if (alertMatches) {
      entitiesCount += alertMatches.length;
      result = result.replace(/\x07/g, '\\a');
    }

    // Escape null character
    const nullMatches = result.match(/\0/g);
    if (nullMatches) {
      entitiesCount += nullMatches.length;
      result = result.replace(/\0/g, '\\0');
    }

    // Escape question marks (to avoid trigraphs)
    const questionMarkMatches = result.match(/\?\?/g);
    if (questionMarkMatches) {
      entitiesCount += questionMarkMatches.length;
      result = result.replace(/\?\?/g, '\\?\\?');
    }

    // Handle other control characters with octal or hex notation
    result = result.replace(/[\x01-\x06\x0E-\x1F\x7F]/g, (match) => {
      const code = match.charCodeAt(0);
      entitiesCount++;
      // Use octal notation for C/C++
      return `\\${code.toString(8).padStart(3, '0')}`;
    });

    // Handle extended ASCII characters
    result = result.replace(/[\x80-\xFF]/g, (match) => {
      const code = match.charCodeAt(0);
      entitiesCount++;
      // Use hex notation for extended ASCII
      return `\\x${code.toString(16).padStart(2, '0')}`;
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapeCpp = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Unescape hex sequences (\xFF)
    result = result.replace(/\\x([0-9a-fA-F]{1,2})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape octal sequences (\123)
    result = result.replace(/\\([0-7]{1,3})/g, (match, oct) => {
      const code = parseInt(oct, 8);
      if (code >= 0 && code <= 255 && oct !== '0') {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape Unicode sequences (\uXXXX) - C++11 and later
    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 65535) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape Unicode sequences (\UXXXXXXXX) - C++11 and later
    result = result.replace(/\\U([0-9a-fA-F]{8})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 0x10FFFF) {
        entitiesCount++;
        if (code <= 65535) {
          return String.fromCharCode(code);
        } else {
          // Handle surrogate pairs for characters > 0xFFFF
          const high = Math.floor((code - 0x10000) / 0x400) + 0xD800;
          const low = ((code - 0x10000) % 0x400) + 0xDC00;
          return String.fromCharCode(high) + String.fromCharCode(low);
        }
      }
      return match;
    });

    // Unescape alert/bell
    const alertEscapeMatches = result.match(/\\a/g);
    if (alertEscapeMatches) {
      entitiesCount += alertEscapeMatches.length;
      result = result.replace(/\\a/g, '\x07');
    }

    // Unescape backspace
    const backspaceEscapeMatches = result.match(/\\b/g);
    if (backspaceEscapeMatches) {
      entitiesCount += backspaceEscapeMatches.length;
      result = result.replace(/\\b/g, '\b');
    }

    // Unescape form feed
    const formFeedEscapeMatches = result.match(/\\f/g);
    if (formFeedEscapeMatches) {
      entitiesCount += formFeedEscapeMatches.length;
      result = result.replace(/\\f/g, '\f');
    }

    // Unescape newlines
    const newlineEscapeMatches = result.match(/\\n/g);
    if (newlineEscapeMatches) {
      entitiesCount += newlineEscapeMatches.length;
      result = result.replace(/\\n/g, '\n');
    }

    // Unescape carriage returns
    const carriageReturnEscapeMatches = result.match(/\\r/g);
    if (carriageReturnEscapeMatches) {
      entitiesCount += carriageReturnEscapeMatches.length;
      result = result.replace(/\\r/g, '\r');
    }

    // Unescape tabs
    const tabEscapeMatches = result.match(/\\t/g);
    if (tabEscapeMatches) {
      entitiesCount += tabEscapeMatches.length;
      result = result.replace(/\\t/g, '\t');
    }

    // Unescape vertical tabs
    const verticalTabEscapeMatches = result.match(/\\v/g);
    if (verticalTabEscapeMatches) {
      entitiesCount += verticalTabEscapeMatches.length;
      result = result.replace(/\\v/g, '\v');
    }

    // Unescape null character
    const nullEscapeMatches = result.match(/\\0/g);
    if (nullEscapeMatches) {
      entitiesCount += nullEscapeMatches.length;
      result = result.replace(/\\0/g, '\0');
    }

    // Unescape question marks
    const questionEscapeMatches = result.match(/\\\\?\?/g);
    if (questionEscapeMatches) {
      entitiesCount += questionEscapeMatches.length;
      result = result.replace(/\\\\?\?/g, '??');
    }

    // Unescape single quotes
    const singleQuoteEscapeMatches = result.match(/\\'/g);
    if (singleQuoteEscapeMatches) {
      entitiesCount += singleQuoteEscapeMatches.length;
      result = result.replace(/\\'/g, "'");
    }

    // Unescape double quotes
    const doubleQuoteEscapeMatches = result.match(/\\"/g);
    if (doubleQuoteEscapeMatches) {
      entitiesCount += doubleQuoteEscapeMatches.length;
      result = result.replace(/\\"/g, '"');
    }

    // Unescape backslashes (must be last)
    const backslashEscapeMatches = result.match(/\\\\/g);
    if (backslashEscapeMatches) {
      entitiesCount += backslashEscapeMatches.length;
      result = result.replace(/\\\\/g, '\\');
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeCpp : unescapeCpp;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeCpp, unescapeCpp]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-cpp.${mode === 'escape' ? 'cpp' : 'txt'}`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleCpp = (): void => {
    setInputText(`// Sample C++ code with special characters
#include <iostream>
#include <string>

int main() {
    // String literals with special characters
    std::string message = "Hello World!\\nWelcome to C++ programming.";
    std::string path = "C:\\\\Users\\\\Documents\\\\file.txt";
    std::string quote = "He said, \\"Programming is fun!\\"";
    
    // Character literals
    char newline = '\\n';
    char tab = '\\t';
    char backslash = '\\\\';
    char singleQuote = '\\'';
    char doubleQuote = '\\"';
    
    // Special escape sequences
    std::string controlChars = "\\a\\b\\f\\r\\v\\0";
    
    // Raw string literal (C++11)
    std::string rawString = R"(This is a "raw" string with \\backslashes and 'quotes')";
    
    // Unicode characters (C++11)
    std::string unicode = "Unicode: \\u03B1\\u03B2\\u03B3"; // α β γ
    std::string emoji = "\\U0001F600"; // 😀
    
    // Multiline string
    std::string multiline = "Line 1\\n"
                           "Line 2\\n"
                           "Line 3";
    
    // Printf format strings
    printf("Name: %s\\nAge: %d\\nScore: %.2f%%\\n", name, age, score);
    
    // Error message with quotes
    std::cerr << "Error: File \\"" << filename << "\\" not found.\\n";
    
    // Hexadecimal and octal escapes
    char hexChar = '\\x41'; // 'A'
    char octChar = '\\101'; // 'A'
    
    return 0;
}`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`// Sample escaped C++ code
#include <iostream>
#include <string>

int main() {
    // String literals with escaped characters
    std::string message = \\"Hello World!\\\\nWelcome to C++ programming.\\";
    std::string path = \\"C:\\\\\\\\Users\\\\\\\\Documents\\\\\\\\file.txt\\";
    std::string quote = \\"He said, \\\\\\"Programming is fun!\\\\\\"\\";
    
    // Character literals
    char newline = \\'\\\\n\\';
    char tab = \\'\\\\t\\';
    char backslash = \\'\\\\\\\\\\';
    char singleQuote = \\'\\\\\\'\\'';
    char doubleQuote = \\'\\\\\\"\\'';
    
    // Special escape sequences
    std::string controlChars = \\"\\\\a\\\\b\\\\f\\\\r\\\\v\\\\0\\";
    
    // Raw string literal (C++11)
    std::string rawString = R\\"(This is a \\\\\\"raw\\\\\\" string with \\\\\\\\backslashes and \\'quotes\\')\\";
    
    // Unicode characters (C++11)
    std::string unicode = \\"Unicode: \\\\u03B1\\\\u03B2\\\\u03B3\\"; // α β γ
    std::string emoji = \\"\\\\U0001F600\\"; // 😀
    
    // Multiline string
    std::string multiline = \\"Line 1\\\\n\\"
                           \\"Line 2\\\\n\\"
                           \\"Line 3\\";
    
    // Printf format strings
    printf(\\"Name: %s\\\\nAge: %d\\\\nScore: %.2f%%\\\\n\\", name, age, score);
    
    // Error message with quotes
    std::cerr << \\"Error: File \\\\\\"\\", << filename << \\"\\\\\\" not found.\\\\n\\";
    
    // Hexadecimal and octal escapes
    char hexChar = \\'\\\\x41\\'; // \\'A\\'
    char octChar = \\'\\\\101\\'; // \\'A\\'
    
    return 0;
}`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-orange-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-red-500 to-orange-600 rounded-lg">
              <Code className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">C/C++ Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced C/C++ string processor with Unicode and trigraph support</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-red-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape C/C++</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-orange-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape C/C++</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-orange-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* C/C++ Escape Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">C/C++ Escape Sequences & Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Basic:</strong> \\\\ \\" \\' \\n \\r \\t
                </div>
                <div className="text-gray-300">
                  <strong>Control:</strong> \\a \\b \\f \\v \\0
                </div>
                <div className="text-gray-300">
                  <strong>Numeric:</strong> \\xFF \\123 (hex/octal)
                </div>
                <div className="text-gray-300">
                  <strong>Unicode:</strong> \\uXXXX \\UXXXXXXXX
                </div>
                <div className="text-gray-300">
                  <strong>Trigraph:</strong> \\?\\? (C legacy)
                </div>
                <div className="text-gray-300">
                  <strong>C++11:</strong> Raw strings, Unicode
                </div>
                <div className="text-gray-300">
                  <strong>Safe:</strong> Surrogate pair handling
                </div>
                <div className="text-gray-300">
                  <strong>Compat:</strong> C89/C++98 to C++20
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Large input/output areas */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".cpp,.c,.h,.hpp,.cc,.cxx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter C/C++ code to escape...' : 'Enter escaped C/C++ code to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed C/C++ output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleCpp}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample C++
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\/\/.*$/gm, '').replace(/\/\*[\s\S]*?\*\//g, '').trim())}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Remove Comments
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/R"([^(]*)\([\s\S]*?\)\1"/g, (match) => '"' + match.slice(match.indexOf('(') + 1, match.lastIndexOf(')')).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"'))}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
            >
              Convert Raw Strings
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CppEscapeUnescapeTool;